import React, { Fragment, useEffect, useRef, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowBack, CloudUpload, GroupAdd } from "@mui/icons-material";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  arrayUnion,
  increment,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { format } from "date-fns";
import { db, auth } from "../../firebase";
import { uploadImage } from "../../utils/awsUtils";
import { useUser } from "../context/UserContext";
import { ImageCropModal } from "../common/ImageCropModal";

const DEPARTMENTS = [
  "Choir",
  "Ushering",
  "Prophets",
  "Media",
  "Children's Church",
  "Youth",
  "Welfare",
  "Protocol",
  "Technical",
  "Evangelism",
  "Drama",
  "Member",
];

const CreateMember = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [department, setDepartment] = useState([]);
  const [favHymns, setFavHymns] = useState("");
  const [favBibleVerse, setFavBibleVerse] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [familyMatches, setFamilyMatches] = useState([]);
  const [checkingFamily, setCheckingFamily] = useState(false);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [familyReviewed, setFamilyReviewed] = useState(false);

  const fileRef = useRef();
  const navigate = useNavigate();
  const { userRole, loading: roleLoading } = useUser();

  useEffect(() => {
    if (!roleLoading && userRole === "operator") {
      navigate("/home");
    }
  }, [roleLoading, userRole, navigate]);

  const toggleMatch = (id) => {
    setSelectedMatchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const findFamilyMatches = async (lastNameValue) => {
    const lastNameLower = lastNameValue.trim().toLowerCase();
    const q = query(collection(db, "members"), where("lastNameLower", "==", lastNameLower));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  // As soon as a usable last name is typed, look up existing members who
  // share it so we can surface them as possible family right away.
  useEffect(() => {
    setSelectedMatchIds([]);
    setFamilyReviewed(false);
    const trimmed = lastName.trim();
    if (trimmed.length < 2) {
      setFamilyMatches([]);
      setCheckingFamily(false);
      return;
    }
    setCheckingFamily(true);
    const timer = setTimeout(async () => {
      try {
        const matches = await findFamilyMatches(trimmed);
        setFamilyMatches(matches);
      } catch (err) {
        setFamilyMatches([]);
      } finally {
        setCheckingFamily(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastName]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setRawImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
    setErrorMsg("");
  };

  const handleCropped = (blob) => {
    setCropModalOpen(false);
    if (blob.size > 2 * 1024 * 1024) {
      setErrorMsg("Cropped image must be under 2MB");
      return;
    }
    setImageFile(new File([blob], "profile.jpg", { type: blob.type || "image/jpeg" }));
    setImagePreview(URL.createObjectURL(blob));
    setErrorMsg("");
  };

  const handleCreate = async () => {
    if (!firstName.trim()) return setErrorMsg("First name cannot be empty");
    if (!lastName.trim()) return setErrorMsg("Last name cannot be empty");
    if (!gender) return setErrorMsg("Please select a gender");
    if (!phone) return setErrorMsg("Phone cannot be empty");
    if (!department.length) return setErrorMsg("Please select at least one department");
    if (!imageFile) return setErrorMsg("Please select a profile image");
    if (familyMatches.length > 0 && !familyReviewed) {
      return setErrorMsg("Please review the possible family matches shown below.");
    }

    setLoading(true);
    try {
      const { imgUrl, imgId } = await uploadImage(imageFile);

      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
      const trimmedEmail = email.trim();

      const memberRef = await addDoc(collection(db, "members"), {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        lastNameLower: trimmedLastName.toLowerCase(),
        name: fullName,
        email: trimmedEmail,
        gender,
        dob: dob ? format(new Date(dob + "T00:00:00"), "d MMM") : "",
        phone,
        department: department.join(", "),
        address: address || "",
        favHymns: favHymns ? `CCC Hymn ${favHymns}` : "",
        favBibleVerse: favBibleVerse || "",
        imgSrc: imgUrl,
        imgId,
        status: "pending",
        familyId: null,
        createdAt: serverTimestamp(),
      });

      // Family linking is best-effort — the member record above already
      // succeeded, so a hiccup here shouldn't look like the whole thing failed.
      try {
        const newMemberEntry = { id: memberRef.id, name: fullName, email: trimmedEmail };
        const selectedMatches = familyMatches.filter((m) => selectedMatchIds.includes(m.id));
        const asFamilyEntry = (m) => ({
          id: m.id,
          name: m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim(),
          email: m.email || "",
        });
        const existingFamilyId = selectedMatches.find((m) => m.familyId)?.familyId;
        let familyId;

        if (existingFamilyId) {
          // Selected relatives already belong to a family — join it, and
          // backfill anyone else selected who was never actually linked.
          const unlinked = selectedMatches.filter((m) => !m.familyId);
          await updateDoc(doc(db, "families", existingFamilyId), {
            members: arrayUnion(newMemberEntry, ...unlinked.map(asFamilyEntry)),
            familyCount: increment(1 + unlinked.length),
          });
          if (unlinked.length > 0) {
            await Promise.all(
              unlinked.map((m) => updateDoc(doc(db, "members", m.id), { familyId: existingFamilyId }))
            );
          }
          familyId = existingFamilyId;
        } else {
          // Either the first relative(s) confirmed have no family record yet,
          // or nobody was selected and this is the first of their family to
          // onboard — either way, start a new family record for them.
          const membersForFamily = [...selectedMatches.map(asFamilyEntry), newMemberEntry];
          const familyDocRef = await addDoc(collection(db, "families"), {
            familyName: trimmedLastName,
            familyCount: membersForFamily.length,
            members: membersForFamily,
            createdAt: serverTimestamp(),
          });
          familyId = familyDocRef.id;
          if (selectedMatches.length > 0) {
            await Promise.all(
              selectedMatches.map((m) => updateDoc(doc(db, "members", m.id), { familyId }))
            );
          }
        }

        await updateDoc(memberRef, { familyId });
      } catch (familyErr) {
        toast("Member added, but family linking couldn't be completed.", { type: "warning" });
      }

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Added Member: ${fullName}`,
        createdAt: serverTimestamp(),
      });

      toast("Member added successfully!", { type: "success" });
      setTimeout(() => navigate("/members"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white";
  const labelClass =
    "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel";

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/members")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">
                Add Member
              </h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">
                Add a new church member to the directory
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && (
              <Alert
                severity="error"
                className="mb-5"
                onClose={() => setErrorMsg("")}
              >
                {errorMsg}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <label className={labelClass}>First Name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="Member's first name"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Last Name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="Member's last name"
                  className={inputClass}
                />
              </Grid>

              {(checkingFamily || familyMatches.length > 0) && (
                <Grid item xs={12} className="!pt-2">
                  {checkingFamily && (
                    <p className="text-xs font-abel text-gray-400">Checking for family matches…</p>
                  )}

                  {!checkingFamily && familyMatches.length > 0 && (
                    <div className="p-3 rounded-xl border border-[#000C66]/20 bg-[#000C66]/5">
                      <p className="text-sm font-abel text-gray-600">
                        We found {familyMatches.length} existing member
                        {familyMatches.length > 1 ? "s" : ""} with the surname "{lastName.trim()}".
                        Some surnames belong to more than one unrelated family — select anyone below
                        who is actually part of this family:
                      </p>

                      <div className="flex flex-col gap-1 mt-2">
                        {familyMatches.map((m) => {
                          const label = m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim();
                          const checked = selectedMatchIds.includes(m.id);
                          return (
                            <label
                              key={m.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-white/60 select-none"
                            >
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={checked}
                                onChange={() => toggleMatch(m.id)}
                              />
                              <span
                                className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
                                  checked ? "bg-[#000C66] border-[#000C66]" : "border-gray-300"
                                }`}
                              >
                                {checked && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                    <path
                                      d="M1.5 5l2.5 2.5 4.5-4.5"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </span>
                              <span className="text-sm font-bold text-gray-800">{label}</span>
                            </label>
                          );
                        })}
                      </div>

                      {!familyReviewed ? (
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedMatchIds([]);
                              setFamilyReviewed(true);
                            }}
                            className="text-xs font-abel px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-white transition"
                          >
                            None of These Are My Family
                          </button>
                          <button
                            type="button"
                            onClick={() => setFamilyReviewed(true)}
                            className="text-xs font-abel px-3 py-1.5 rounded-lg bg-[#000C66] text-white hover:bg-blue-900 transition"
                          >
                            Confirm Selection
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-2">
                          <p
                            className={`text-xs font-abel ${
                              selectedMatchIds.length ? "text-green-700" : "text-gray-500"
                            }`}
                          >
                            {selectedMatchIds.length > 0
                              ? `✓ Linking with ${selectedMatchIds.length} selected member${
                                  selectedMatchIds.length > 1 ? "s" : ""
                                }.`
                              : "Noted — registering as a separate family."}
                          </p>
                          <button
                            type="button"
                            onClick={() => setFamilyReviewed(false)}
                            className="text-xs font-abel underline text-gray-400"
                          >
                            Change
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Gender *</label>
                <select
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                >
                  <option value="">-- Select Gender --</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Date of Birth</label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => {
                    setDob(e.target.value);
                    setErrorMsg("");
                  }}
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="+234 000 000 0000"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="member@example.com"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => { setAddress(e.target.value); setErrorMsg(""); }}
                  placeholder="e.g. 12 Church Street, Lagos"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Department * <span className="text-gray-300 normal-case">(select all that apply)</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                  {DEPARTMENTS.map((d) => (
                    <label
                      key={d}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition text-sm font-abel select-none ${
                        department.includes(d)
                          ? "border-[#000C66] bg-[#000C66]/5 text-[#000C66] font-semibold"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={department.includes(d)}
                        onChange={() => {
                          setDepartment((prev) =>
                            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
                          );
                          setErrorMsg("");
                        }}
                      />
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
                          department.includes(d)
                            ? "bg-[#000C66] border-[#000C66]"
                            : "border-gray-300"
                        }`}
                      >
                        {department.includes(d) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                      {d}
                    </label>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Favourite Hymn</label>
                <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#000C66] focus-within:ring-2 focus-within:ring-[#000C66]/10 transition bg-white">
                  <span className="px-3 py-3 bg-gray-50 border-r border-gray-200 text-gray-500 text-sm font-abel whitespace-nowrap select-none">CCC Hymn</span>
                  <input
                    type="number"
                    min="1"
                    value={favHymns}
                    onChange={(e) => { setFavHymns(e.target.value); setErrorMsg(""); }}
                    placeholder="767"
                    className="flex-1 px-3 py-3 text-sm font-abel text-gray-700 bg-white focus:outline-none"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <label className={labelClass}>Favourite Bible Verse</label>
                <input
                  type="text"
                  value={favBibleVerse}
                  onChange={(e) => {
                    setFavBibleVerse(e.target.value);
                    setErrorMsg("");
                  }}
                  placeholder="e.g., John 3:16"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>
                  Profile Image *{" "}
                  <span className="text-gray-300 normal-case">(max 2MB)</span>
                </label>
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="preview"
                    className="h-24 w-24 object-cover rounded-xl border border-gray-100 mb-2"
                  />
                )}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#000C66] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload
                    className="text-gray-300"
                    style={{ fontSize: 24 }}
                  />
                  <p className="text-xs text-gray-400 font-abel mt-1">
                    {imageFile ? imageFile.name : "Click to select photo"}
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <GroupAdd style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Adding..." : "Add Member"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>

      <ImageCropModal
        open={cropModalOpen}
        imageSrc={rawImageSrc}
        aspect={1}
        onCancel={() => setCropModalOpen(false)}
        onCropped={handleCropped}
      />
    </Fragment>
  );
};

export default CreateMember;
